import type { RouterInputs, RouterOutputs } from "@/lib/api"

export type ChangelogCandidates = RouterOutputs["linear"]["changelogCandidates"]
export type ChangelogSelection = NonNullable<
	RouterInputs["linear"]["getCompletedIssuesForChangelog"]["selection"]
>

export type PickerItemKind = "project" | "issue" | "pullRequest"

export interface PickerItem {
	key: string
	kind: PickerItemKind
	id: string
	title: string
	reference: string | null
	completedAt: string | null
	requestCount: number
	labels: { name: string; color?: string | null }[]
	internal: boolean
}

export type PickerRange = "lastRelease" | "week" | "custom"

const DAY_MS = 24 * 60 * 60 * 1000

/** The window to ask for. The last-release range sends nothing, so the
 * server picks the start from the newest published changelog. */
export function rangeWindow(
	range: PickerRange,
	custom: { from?: Date; to?: Date } | undefined,
	now = Date.now(),
): { since: string | null; until: string | null } | null {
	if (range === "lastRelease") return { since: null, until: null }
	if (range === "week") {
		return { since: new Date(now - 7 * DAY_MS).toISOString(), until: null }
	}
	if (!custom?.from) return null
	const start = new Date(custom.from)
	start.setHours(0, 0, 0, 0)
	const end = new Date(custom.to ?? custom.from)
	end.setHours(23, 59, 59, 999)
	return { since: start.toISOString(), until: end.toISOString() }
}

/** "Completed today", "Merged Oct 23", with the year only when it differs. */
export function doneLabel(
	item: Pick<PickerItem, "kind" | "completedAt">,
	now = new Date(),
): string | null {
	if (!item.completedAt) return null
	const date = new Date(item.completedAt)
	const verb = item.kind === "pullRequest" ? "Merged" : "Completed"
	const dayStart = (d: Date) =>
		new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
	const days = Math.round((dayStart(now) - dayStart(date)) / DAY_MS)
	if (days === 0) return `${verb} today`
	if (days === 1) return `${verb} yesterday`
	return `${verb} ${date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
	})}`
}

export function newestFirst(items: PickerItem[]): PickerItem[] {
	return [...items].sort((a, b) =>
		(b.completedAt ?? "").localeCompare(a.completedAt ?? ""),
	)
}

const INTERNAL_LABELS = new Set([
	"chore",
	"internal",
	"refactor",
	"tech debt",
	"techdebt",
	"infra",
	"infrastructure",
	"dependencies",
	"deps",
	"test",
	"tests",
	"testing",
	"ci",
	"devops",
	"maintenance",
])

const INTERNAL_PR_TITLE =
	/^(?:(?:chore|refactor|tests?|ci|build|deps|style)(?:\([^)]*\))?!?:|bump\s)/i

/** Work customers never see starts unticked; the teammate can still tick it. */
export function isLikelyInternal(item: {
	kind: PickerItemKind
	title: string
	labels: { name: string }[]
}): boolean {
	if (
		item.labels.some((label) =>
			INTERNAL_LABELS.has(label.name.trim().toLowerCase()),
		)
	) {
		return true
	}
	return (
		item.kind === "pullRequest" && INTERNAL_PR_TITLE.test(item.title.trim())
	)
}

export function toPickerItems(candidates: ChangelogCandidates): PickerItem[] {
	const items: Omit<PickerItem, "internal">[] = [
		...candidates.projects.map((project) => ({
			key: `project:${project.id}`,
			kind: "project" as const,
			id: project.id,
			title: project.name,
			reference: null,
			completedAt: project.completedAt,
			requestCount: project.requestCount,
			labels: [],
		})),
		...candidates.issues.map((issue) => ({
			key: `issue:${issue.id}`,
			kind: "issue" as const,
			id: issue.id,
			title: issue.title,
			reference: issue.identifier,
			completedAt: issue.completedAt,
			requestCount: issue.requestCount,
			labels: issue.labels,
		})),
		...candidates.pullRequests.map((pr) => ({
			key: `pullRequest:${pr.number}`,
			kind: "pullRequest" as const,
			id: String(pr.number),
			title: pr.title,
			reference: `#${pr.number}`,
			completedAt: pr.mergedAt,
			requestCount: 0,
			labels: [],
		})),
	]
	return items.map((item) => ({ ...item, internal: isLikelyInternal(item) }))
}

export function defaultSelectedKeys(items: PickerItem[]): Set<string> {
	return new Set(items.filter((item) => !item.internal).map((item) => item.key))
}

const KIND_WEIGHT: Record<PickerItemKind, number> = {
	project: 0,
	issue: 1,
	pullRequest: 2,
}

/** The pinned item if it is still picked, else the most asked-for one. */
export function resolveLead(
	items: PickerItem[],
	selected: Set<string>,
	pinned: string | null,
): PickerItem | null {
	const picked = items.filter((item) => selected.has(item.key))
	const pin = picked.find((item) => item.key === pinned)
	if (pin) return pin
	return (
		[...picked].sort(
			(a, b) =>
				b.requestCount - a.requestCount ||
				KIND_WEIGHT[a.kind] - KIND_WEIGHT[b.kind] ||
				(b.completedAt ?? "").localeCompare(a.completedAt ?? ""),
		)[0] ?? null
	)
}

export function buildSelection({
	since,
	items,
	selected,
	lead,
}: {
	since: string
	items: PickerItem[]
	selected: Set<string>
	lead: PickerItem | null
}): ChangelogSelection {
	const picked = items.filter((item) => selected.has(item.key))
	const idsOf = (kind: PickerItemKind) =>
		picked.filter((item) => item.kind === kind).map((item) => item.id)
	return {
		since,
		projectIds: idsOf("project"),
		issueIds: idsOf("issue"),
		pullRequestNumbers: idsOf("pullRequest").map(Number),
		lead: lead ? { kind: lead.kind, id: lead.id } : null,
	}
}

export function selectionSummary(items: PickerItem[], selected: Set<string>) {
	const count = (kind: PickerItemKind) =>
		items.filter((item) => item.kind === kind && selected.has(item.key)).length
	const parts: string[] = []
	const projects = count("project")
	const issues = count("issue")
	const prs = count("pullRequest")
	if (projects)
		parts.push(`${projects} ${projects === 1 ? "project" : "projects"}`)
	if (issues) parts.push(`${issues} ${issues === 1 ? "issue" : "issues"}`)
	if (prs) parts.push(`${prs} ${prs === 1 ? "pull request" : "pull requests"}`)
	return { total: projects + issues + prs, parts }
}
